import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro } from "@/admin/ui";
import { motion } from "framer-motion";
import { Plus, ArrowRight, CalendarRange, MapPin, Archive, Search } from "lucide-react";

const STATUS_META = {
  upcoming: { label: "Upcoming", cls: "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]" },
  live: { label: "Live", cls: "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]" },
  planning: { label: "Planning", cls: "bg-[hsl(var(--secondary)/0.15)] text-[hsl(var(--secondary))]" },
  draft: { label: "Draft", cls: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]" },
  past: { label: "Completed", cls: "bg-[hsl(var(--pc-gold-2)/0.25)] text-[hsl(38_40%_32%)]" },
  completed: { label: "Completed", cls: "bg-[hsl(var(--pc-gold-2)/0.25)] text-[hsl(38_40%_32%)]" },
  archived: { label: "Archived", cls: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]" },
  future: { label: "Future", cls: "bg-white/85 text-[hsl(var(--secondary))]" },
};
const FILTERS = ["all", "upcoming", "planning", "past", "future", "draft"];

export default function Events() {
  const [events, setEvents] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { setAdminEvent } = useAdminEvent();
  const ctx = useOutletContext();

  useEffect(() => {
    api.listEvents().then((evs) => {
      setEvents(evs);
      evs.forEach((e) => {
        if (e.status === "future") return;
        api.commandCenter(e.id).then((ov) => setMetrics((m) => ({ ...m, [e.id]: ov }))).catch(() => {});
      });
    });
  }, []);

  const open = (ev) => { setAdminEvent(ev.id); navigate("/manager/overview"); };

  const shown = useMemo(() => events.filter((e) => {
    if (filter !== "all") {
      if (filter === "past" && !["past", "completed"].includes(e.status)) return false;
      if (filter !== "past" && e.status !== filter) return false;
    }
    if (q && !`${e.name} ${e.destination || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [events, filter, q]);

  return (
    <div>
      <PageIntro>All Collabera events in one place. Open an event to enter its independent workspace — every module is scoped to that event.</PageIntro>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <input data-testid="admin-events-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events by name or location" className="w-full pl-9 rounded-lg border border-[hsl(var(--border))] bg-white px-3 py-2 text-sm" />
        </div>
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`admin-events-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${filter === f ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{f}</button>
          ))}
        </div>
      </div>

      <div data-testid="admin-events-gallery-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((ev, i) => {
          const sm = STATUS_META[ev.status] || STATUS_META.draft;
          const ov = metrics[ev.id];
          const past = ["past", "completed"].includes(ev.status);
          return (
            <motion.button
              key={ev.id} data-testid="admin-events-gallery-event-card"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.05 }}
              onClick={() => open(ev)}
              className="premium-card overflow-hidden flex flex-col text-left"
            >
              <div className="relative h-32">
                <img src={ev.hero_image} alt={ev.name} className={`absolute inset-0 h-full w-full object-cover ${past ? "grayscale-[35%]" : ""}`} />
                <div className="absolute inset-0 hero-fade" />
                <div className="absolute top-3 left-3"><span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${sm.cls}`}>{past && <Archive className="h-3 w-3" />}{sm.label}</span></div>
                <div className="absolute bottom-3 left-4 right-4 text-white"><h3 className="font-serif text-xl leading-tight">{ev.name}</h3></div>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><CalendarRange className="h-4 w-4" /> {ev.date_label}</div>
                {ev.destination && ev.destination !== "TBD" && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {ev.destination}</div>}
                <div className="flex items-center gap-3 mt-3 text-xs text-[hsl(var(--muted-foreground))]">
                  <span><span className="font-serif text-base text-[hsl(var(--primary))]">{ov ? ov.snapshot.attendees : (ev.status === "future" ? "—" : 0)}</span> attendees</span>
                  {ov && ov.readiness && <span><span className="font-serif text-base text-[hsl(var(--primary))]">{Math.round(ov.readiness.reduce((a, r) => a + r.value, 0) / ov.readiness.length)}%</span> ready</span>}
                </div>
                <div className="flex-1" />
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--secondary))]">Open workspace <ArrowRight className="h-4 w-4" /></span>
              </div>
            </motion.button>
          );
        })}

        <button data-testid="admin-events-gallery-create-card" onClick={() => ctx?.openCreate?.()} className="rounded-[1.25rem] border-2 border-dashed border-[hsl(var(--border))] bg-white/40 p-6 flex flex-col items-center justify-center text-center min-h-[15rem] hover:border-[hsl(var(--secondary))] hover:bg-white transition-colors">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))] mb-3"><Plus className="h-6 w-6" /></div>
          <div data-testid="admin-events-gallery-create-button" className="font-serif text-xl text-[hsl(var(--primary))]">Create New Event</div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 max-w-[12rem]">Spin up an independent workspace for any Collabera event.</p>
        </button>
      </div>
    </div>
  );
}
